import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShoppingBag } from "lucide-react";
import PageBanner from "../components/PageBanner";
import { useCart } from "../lib/CartContext";
import api from "../lib/api";
import SEO from "../lib/SEO";
import { toast } from "sonner";

export default function ShopCategory() {
    const { category: categorySlug } = useParams();
    const [category, setCategory] = useState(null);
    const [products, setProducts] = useState([]);
    const [notFound, setNotFound] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const { addItem } = useCart();

    useEffect(() => {
        setLoaded(false);
        api.get(`/shop-categories/${categorySlug}`)
            .then((r) => {
                setCategory(r.data);
                return api.get(`/products?category=${encodeURIComponent(r.data.name)}`);
            })
            .then((r) => setProducts(Array.isArray(r.data) ? r.data : []))
            .catch(() => setNotFound(true))
            .finally(() => setLoaded(true));
    }, [categorySlug]);

    const handleAdd = (e, product) => {
        e.preventDefault();
        addItem(product, 1);
        toast.success(`${product.name} added to cart`);
    };

    if (notFound) {
        return (
            <main className="pt-32 pb-20 max-w-3xl mx-auto px-4 text-center" data-testid="shop-category-notfound">
                <h2 className="font-display text-3xl mb-4">Category not found</h2>
                <Link to="/shop" className="btn-pink mt-4">Back to shop</Link>
            </main>
        );
    }
    if (!category) {
        return <main className="pt-32 pb-20 text-center text-ink-soft">Loading…</main>;
    }

    return (
        <main data-testid="shop-category-page">
            <SEO
                title={`${category.name} — Cake Central`}
                description={category.description || `Order ${category.name.toLowerCase()} online from Cake Central.`}
                image={category.cover_image_url || undefined}
                path={`/shop/${category.slug}`}
            />
            <PageBanner
                eyebrow="Order online"
                title={category.name}
                image={category.cover_image_url || "https://images.unsplash.com/photo-1535141192574-5d4897c12636?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"}
            />

            <section className="bg-[#FFF7FB] py-10 md:py-14">
                <div className="px-4 sm:px-6 lg:px-10">
                    <Link
                        to="/shop"
                        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink-soft hover:text-blush mb-6 transition-colors"
                        data-testid="back-to-shop"
                    >
                        <ArrowLeft size={14} /> All categories
                    </Link>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5 max-w-screen-2xl mx-auto">
                        {products.map((p, i) => (
                            <Link
                                key={p.id}
                                to={`/shop/product/${p.slug}`}
                                className="group block bg-white card-lift overflow-hidden"
                                data-testid={`shop-product-${i}`}
                            >
                                <div className="aspect-[4/5] overflow-hidden bg-blush-pale">
                                    <img
                                        src={p.image_url}
                                        alt={p.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="p-5">
                                    <h3 className="font-display text-xl text-ink mb-2 leading-tight">{p.name}</h3>
                                    <p className="text-xs text-ink-soft line-clamp-2 mb-4">{p.short_description}</p>
                                    <div className="flex items-center justify-between gap-2">
                                        <div>
                                            <span className="font-display text-2xl text-ink">${Number(p.price).toFixed(2)}</span>
                                            {Array.isArray(p.price_tiers) && p.price_tiers.length > 0 && (
                                                <p className="text-[10px] text-blush tracking-[0.1em] mt-0.5">
                                                    {p.price_tiers.map((t) => `${t.qty} for $${Number(t.price).toFixed(0)}`).join(" · ")}
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => handleAdd(e, p)}
                                            className="inline-flex items-center gap-2 px-3 py-2 bg-blush text-white text-[10px] tracking-[0.2em] uppercase hover:bg-[#C4007A] transition-colors shrink-0"
                                            data-testid={`add-to-cart-${i}`}
                                        >
                                            <ShoppingBag size={12} /> Add
                                        </button>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {loaded && products.length === 0 && (
                        <p className="text-center text-ink-soft py-20">New products coming to this category soon.</p>
                    )}
                </div>
            </section>
        </main>
    );
}
